
import React, { useState, useEffect } from "react";
import { ManagementDashboard } from "@/components/professional/dashboard/management/ManagementDashboard";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Database, Briefcase, Users, Archive, Settings, ChevronRight } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface MobileProfessionalManagementProps {
  activeTab?: string;
  section?: string;
}

export default function MobileProfessionalManagement({ activeTab, section }: MobileProfessionalManagementProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState<string>(activeTab || "establishment");
  const [selectedSection, setSelectedSection] = useState<string | undefined>(section);
  
  useEffect(() => {
    if (activeTab) {
      setSelectedTab(activeTab);
    }
    if (section) {
      setSelectedSection(section);
    }
  }, [activeTab, section]);

  const managementSections = [
    { id: "establishment", label: "Établissement", icon: Briefcase },
    { id: "staff", label: "Personnel", icon: Users },
    { id: "inventory", label: "Inventaire", icon: Archive },
    { id: "settings", label: "Paramètres", icon: Settings },
  ];

  // If we're on a child route, show the full management dashboard with the proper tab selected
  if (location.pathname !== "/professional-mobile/management") {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="bg-white p-4 sticky top-0 z-10 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/professional-mobile/management")}
              aria-label="Retour"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold">
              {managementSections.find(section => section.id === selectedTab)?.label || "Gestion de l'établissement"}
            </h1>
          </div>
        </div>
        <main className="flex-1 p-2">
          <ScrollArea className="h-[calc(100vh-100px)]">
            <ManagementDashboard initialTab={selectedTab} />
          </ScrollArea>
        </main>
      </div>
    );
  }

  // On main route, show a list of management sections to navigate to
  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-white p-4 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/professional-mobile")}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold">Gestion de l'établissement</h1>
        </div>
      </div>
      <main className="flex-1 p-4">
        <div className="space-y-3">
          {managementSections.map((section) => (
            <Card 
              key={section.id}
              className={cn("cursor-pointer transition-colors hover:bg-gray-50")}
              onClick={() => navigate(`/professional-mobile/management/${section.id === "establishment" ? "" : section.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center">
                      <section.icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">{section.label}</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}
